import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DomainSyncStatus, IntegrationSyncStatus } from '@lizrd/contracts';
import { getSyncStatus } from '../lib/api';
import { useUiStore } from '../store/ui';
import { Progress } from './ui/progress';
import { Spinner } from './ui/spinner';

const ACTIVE_RUN = new Set(['QUEUED', 'RUNNING']);
const isActive = (s: IntegrationSyncStatus) =>
  s.status === 'SYNCING' || ACTIVE_RUN.has(s.runStatus ?? '');
const inFlight = (d: DomainSyncStatus) =>
  d.integrations.some(isActive) || d.graph.stage === 'PENDING' || d.graph.stage === 'RUNNING';

/**
 * Global, reusable sync-progress banner. Tracks whichever domain last
 * triggered a sync (via the UI store), shows compact live progress at the top of
 * the app, and smoothly collapses away a moment after the sync settles. Renders
 * nothing when no sync is being tracked.
 */
export function SyncBanner() {
  const envId = useUiStore((s) => s.activeSyncEnvId);
  const clear = useUiStore((s) => s.clearActiveSyncEnv);
  const [open, setOpen] = useState(false);

  const status = useQuery({
    queryKey: ['sync-status', envId],
    queryFn: () => getSyncStatus(envId as string),
    enabled: !!envId,
    refetchInterval: (q) => (q.state.data && inFlight(q.state.data) ? 1500 : false),
  });

  useEffect(() => {
    if (envId) setOpen(true);
  }, [envId]);

  const data = status.data;
  const settled = data ? !inFlight(data) : false;
  useEffect(() => {
    if (envId && settled) {
      const t = setTimeout(() => setOpen(false), 1800);
      return () => clearTimeout(t);
    }
  }, [envId, settled]);

  if (!envId) return null;

  const rows = data?.integrations ?? [];
  const doneCount = rows.filter((s) => !isActive(s)).length;
  const avg = rows.length
    ? Math.round(rows.reduce((n, s) => n + s.progressPct, 0) / rows.length)
    : 0;
  const failed = rows.some((s) => s.runStatus === 'FAILED') || data?.graph.stage === 'FAILED';
  const busy = data ? inFlight(data) : true;

  return (
    <div
      onTransitionEnd={() => {
        if (!open) clear();
      }}
      className={`overflow-hidden border-b border-hairline bg-surface-2 transition-all duration-500 ease-out ${
        open ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="mx-auto flex w-full max-w-4xl items-center gap-3 px-6 py-2">
        {busy ? (
          <Spinner size="sm" />
        ) : (
          <span className={failed ? 'text-danger' : 'text-iris'}>{failed ? '✕' : '✓'}</span>
        )}
        <span className="text-sm font-medium text-bone">
          {busy ? 'Syncing your domain…' : failed ? 'Sync finished with issues' : 'Sync complete'}
        </span>
        {busy && rows.length > 0 && (
          <span className="text-xs text-muted">
            {doneCount}/{rows.length} sources
          </span>
        )}
        <div className="ml-auto w-40">
          <Progress
            value={busy ? Math.max(5, avg) : 100}
            size="sm"
            variant={failed ? 'danger' : 'iris'}
          />
        </div>
      </div>
    </div>
  );
}
